import React, { useState } from 'react';
import { Input, Button, Space, Card, Table, Empty, Radio } from 'antd';
import { SearchOutlined, ClearOutlined, PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { useC305Query } from './hooks/useC305Query';
import type {
  CustomerStatRow, ProductStatRow, DeptStatRow, SalesCustomerStatRow,
  CurrencySummary, UnitSummary,
} from '../../services/c305Api';
import './C305Form.css';

const docTypeLabels: Record<string, string> = {
  bulk: '大貨單', sample: '樣品單', all: '全部', inventory: '庫存單',
};

const C305Form: React.FC = () => {
  const {
    loading, customerData, productData, deptData, salesCustomerData,
    currencySummary, unitSummary, queried, noData, currentType,
    runQuery, reset,
  } = useC305Query();

  const [reportType, setReportType] = useState<string>('order_customer');
  const [docType, setDocType] = useState<string>('bulk');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [salesCustFrom, setSalesCustFrom] = useState('');
  const [salesCustTo, setSalesCustTo] = useState('');
  const [orderCustFrom, setOrderCustFrom] = useState('');
  const [orderCustTo, setOrderCustTo] = useState('');
  const [productFrom, setProductFrom] = useState('');
  const [productTo, setProductTo] = useState('');
  const [deptFrom, setDeptFrom] = useState('');
  const [deptTo, setDeptTo] = useState('');
  const [deliveryFrom, setDeliveryFrom] = useState('');
  const [deliveryTo, setDeliveryTo] = useState('');

  // BR-U01/U04: sales customer disabled for bulk/inventory
  const salesCustDisabled = docType === 'bulk' || docType === 'inventory';

  const handleQuery = () => {
    runQuery({
      report_type: reportType,
      doc_type: docType,
      date_from: dateFrom.trim() || undefined,
      date_to: dateTo.trim() || undefined,
      sales_customer_from: salesCustDisabled ? undefined : (salesCustFrom.trim() || undefined),
      sales_customer_to: salesCustDisabled ? undefined : (salesCustTo.trim() || undefined),
      order_customer_from: orderCustFrom.trim() || undefined,
      order_customer_to: orderCustTo.trim() || undefined,
      product_from: productFrom.trim() || undefined,
      product_to: productTo.trim() || undefined,
      dept_from: deptFrom.trim() || undefined,
      dept_to: deptTo.trim() || undefined,
      delivery_no_from: deliveryFrom.trim() || undefined,
      delivery_no_to: deliveryTo.trim() || undefined,
    });
  };

  const handleReset = () => {
    setReportType('order_customer');
    setDocType('bulk');
    setDateFrom(''); setDateTo('');
    setSalesCustFrom(''); setSalesCustTo('');
    setOrderCustFrom(''); setOrderCustTo('');
    setProductFrom(''); setProductTo('');
    setDeptFrom(''); setDeptTo('');
    setDeliveryFrom(''); setDeliveryTo('');
    reset();
  };

  const hasData = currentType === 'order_customer' ? customerData.length > 0
    : currentType === 'product' ? productData.length > 0
    : currentType === 'department' ? deptData.length > 0
    : salesCustomerData.length > 0;

  const handlePrint = () => {
    if (!hasData) return;
    const typeLabel = currentType === 'order_customer' ? '下單客戶別'
      : currentType === 'product' ? '產品別'
      : currentType === 'department' ? '部門別' : '業績客戶別';
    const title = `交運統計表（${typeLabel}）`;
    const win = window.open('', '_blank');
    if (!win) return;
    const { headers, rows, count } = buildPrintData();
    win.document.write(`<!DOCTYPE html><html><head><title>${title}</title>
<style>body{font-family:'Microsoft JhengHei',sans-serif;margin:20px;font-size:11px}h2{text-align:center}table{width:100%;border-collapse:collapse}th,td{border:1px solid #333;padding:2px 5px}th{background:#f0f0f0;text-align:center}.right{text-align:right}@media print{body{margin:10mm}}</style></head><body>
<h2>${title}</h2>
<table><thead><tr>${headers}</tr></thead><tbody>${rows}</tbody></table>
<p style="margin-top:8px">共 ${count} 筆</p>
</body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  const buildPrintData = () => {
    if (currentType === 'product') {
      return {
        headers: '<th>產品代號</th><th>產品說明</th><th>單位</th><th>幣別</th><th>數量</th><th>金額</th><th>台幣金額</th>',
        rows: productData.map(r => `<tr><td>${r.product_code}</td><td>${r.product_desc||''}</td><td>${r.unit}</td><td>${r.currency}</td><td class="right">${r.total_qty?.toFixed(2)}</td><td class="right">${r.total_amt?.toFixed(2)}</td><td class="right">${r.total_nt_amt?.toFixed(0)}</td></tr>`).join(''),
        count: productData.length,
      };
    }
    if (currentType === 'department') {
      return {
        headers: '<th>部門</th><th>客戶</th><th>幣別</th><th>交運總額</th><th>台幣金額</th>',
        rows: deptData.map(r => `<tr><td>${r.dept_name||r.dept_code}</td><td>${r.customer_code}</td><td>${r.currency}</td><td class="right">${r.total_amt?.toFixed(2)}</td><td class="right">${r.total_nt_amt?.toFixed(0)}</td></tr>`).join(''),
        count: deptData.length,
      };
    }
    if (currentType === 'sales_customer') {
      return {
        headers: '<th>業績客戶</th><th>下單客戶</th><th>幣別</th><th>筆數</th><th>交運總額</th><th>台幣金額</th>',
        rows: salesCustomerData.map(r => `<tr><td>${r.sales_customer_name||r.sales_customer_code}</td><td>${r.order_customer_code}</td><td>${r.currency}</td><td class="right">${r.delivery_count}</td><td class="right">${r.total_amt?.toFixed(2)}</td><td class="right">${r.total_nt_amt?.toFixed(0)}</td></tr>`).join(''),
        count: salesCustomerData.length,
      };
    }
    return {
      headers: '<th>下單客戶</th><th>客戶名稱</th><th>幣別</th><th>交運筆數</th><th>交運總額</th><th>台幣金額</th>',
      rows: customerData.map(r => `<tr><td>${r.customer_code}</td><td>${r.customer_name||''}</td><td>${r.currency}</td><td class="right">${r.delivery_count}</td><td class="right">${r.total_amt?.toFixed(2)}</td><td class="right">${r.total_nt_amt?.toFixed(0)}</td></tr>`).join(''),
      count: customerData.length,
    };
  };

  const customerColumns: ColumnsType<CustomerStatRow> = [
    { title: '下單客戶', dataIndex: 'customer_code', key: 'customer_code', width: 100 },
    { title: '客戶名稱', dataIndex: 'customer_name', key: 'customer_name', ellipsis: true, width: 150 },
    { title: '幣別', dataIndex: 'currency', key: 'currency', width: 60 },
    { title: '交運筆數', dataIndex: 'delivery_count', key: 'delivery_count', width: 90, align: 'right' as const },
    { title: '交運總額', dataIndex: 'total_amt', key: 'total_amt', width: 120, align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '台幣金額', dataIndex: 'total_nt_amt', key: 'total_nt_amt', width: 120, align: 'right' as const, render: (v: number) => v?.toFixed(0) },
  ];

  const productColumns: ColumnsType<ProductStatRow> = [
    { title: '產品代號', dataIndex: 'product_code', key: 'product_code', width: 130 },
    { title: '產品說明', dataIndex: 'product_desc', key: 'product_desc', ellipsis: true, width: 160 },
    { title: '單位', dataIndex: 'unit', key: 'unit', width: 60 },
    { title: '幣別', dataIndex: 'currency', key: 'currency', width: 60 },
    { title: '數量', dataIndex: 'total_qty', key: 'total_qty', width: 100, align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '金額', dataIndex: 'total_amt', key: 'total_amt', width: 120, align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '台幣金額', dataIndex: 'total_nt_amt', key: 'total_nt_amt', width: 120, align: 'right' as const, render: (v: number) => v?.toFixed(0) },
  ];

  const deptColumns: ColumnsType<DeptStatRow> = [
    { title: '部門代號', dataIndex: 'dept_code', key: 'dept_code', width: 80 },
    { title: '部門名稱', dataIndex: 'dept_name', key: 'dept_name', ellipsis: true, width: 120 },
    { title: '客戶', dataIndex: 'customer_code', key: 'customer_code', width: 100 },
    { title: '幣別', dataIndex: 'currency', key: 'currency', width: 60 },
    { title: '交運總額', dataIndex: 'total_amt', key: 'total_amt', width: 120, align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '台幣金額', dataIndex: 'total_nt_amt', key: 'total_nt_amt', width: 120, align: 'right' as const, render: (v: number) => v?.toFixed(0) },
  ];

  const salesColumns: ColumnsType<SalesCustomerStatRow> = [
    { title: '業績客戶', dataIndex: 'sales_customer_code', key: 'sales_customer_code', width: 100 },
    { title: '業績客戶名', dataIndex: 'sales_customer_name', key: 'sales_customer_name', ellipsis: true, width: 130 },
    { title: '下單客戶', dataIndex: 'order_customer_code', key: 'order_customer_code', width: 100 },
    { title: '幣別', dataIndex: 'currency', key: 'currency', width: 60 },
    { title: '交運筆數', dataIndex: 'delivery_count', key: 'delivery_count', width: 90, align: 'right' as const },
    { title: '交運總額', dataIndex: 'total_amt', key: 'total_amt', width: 120, align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '台幣金額', dataIndex: 'total_nt_amt', key: 'total_nt_amt', width: 120, align: 'right' as const, render: (v: number) => v?.toFixed(0) },
  ];

  const currencySummaryColumns: ColumnsType<CurrencySummary> = [
    { title: '幣別', dataIndex: 'currency', key: 'currency', width: 80 },
    { title: '合計金額', dataIndex: 'total_amt', key: 'total_amt', align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '台幣合計', dataIndex: 'total_nt_amt', key: 'total_nt_amt', align: 'right' as const, render: (v: number) => v?.toFixed(0) },
  ];

  const unitSummaryColumns: ColumnsType<UnitSummary> = [
    { title: '單位', dataIndex: 'unit', key: 'unit', width: 60 },
    { title: '幣別', dataIndex: 'currency', key: 'currency', width: 60 },
    { title: '合計數量', dataIndex: 'total_qty', key: 'total_qty', align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '合計金額', dataIndex: 'total_amt', key: 'total_amt', align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '台幣合計', dataIndex: 'total_nt_amt', key: 'total_nt_amt', align: 'right' as const, render: (v: number) => v?.toFixed(0) },
  ];

  const fl = (text: string) => (
    <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>{text}</label>
  );

  const renderTable = () => {
    if (currentType === 'product') {
      return <Table<ProductStatRow> columns={productColumns} dataSource={productData} rowKey={(r, i) => `${r.product_code}-${r.unit}-${r.currency}-${i}`} loading={loading} size="small" pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }} scroll={{ x: 800 }} style={{ marginBottom: 16 }} />;
    }
    if (currentType === 'department') {
      return <Table<DeptStatRow> columns={deptColumns} dataSource={deptData} rowKey={(r, i) => `${r.dept_code}-${r.customer_code}-${r.currency}-${i}`} loading={loading} size="small" pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }} scroll={{ x: 700 }} style={{ marginBottom: 16 }} />;
    }
    if (currentType === 'sales_customer') {
      return <Table<SalesCustomerStatRow> columns={salesColumns} dataSource={salesCustomerData} rowKey={(r, i) => `${r.sales_customer_code}-${r.order_customer_code}-${r.currency}-${i}`} loading={loading} size="small" pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }} scroll={{ x: 800 }} style={{ marginBottom: 16 }} />;
    }
    return <Table<CustomerStatRow> columns={customerColumns} dataSource={customerData} rowKey={(r, i) => `${r.customer_code}-${r.currency}-${i}`} loading={loading} size="small" pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }} scroll={{ x: 700 }} style={{ marginBottom: 16 }} />;
  };

  const renderSummary = () => {
    if (currentType === 'product' && unitSummary.length > 0) {
      return (<><div style={{ marginBottom: 6, fontWeight: 600 }}>單位合計</div><Table<UnitSummary> columns={unitSummaryColumns} dataSource={unitSummary} rowKey={(r) => `${r.unit}-${r.currency}`} size="small" pagination={false} scroll={{ x: 400 }} /></>);
    }
    if (currentType !== 'product' && currencySummary.length > 0) {
      return (<><div style={{ marginBottom: 6, fontWeight: 600 }}>幣別合計</div><Table<CurrencySummary> columns={currencySummaryColumns} dataSource={currencySummary} rowKey="currency" size="small" pagination={false} scroll={{ x: 300 }} /></>);
    }
    return null;
  };

  return (
    <PermissionGuard module={Modules.C305} action="query">
      <div className="c305-container">
        <div className="c305-header">
          <h2>交運統計表</h2>
          {queried && !noData && hasData && (
            <Button icon={<PrinterOutlined />} onClick={handlePrint}>列印</Button>
          )}
        </div>
        <Card size="small" style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 12 }}>
            {fl('報表類型')}
            <Radio.Group value={reportType} onChange={e => setReportType(e.target.value)}>
              <Radio.Button value="order_customer">下單客戶別</Radio.Button>
              <Radio.Button value="product">產品別</Radio.Button>
              <Radio.Button value="department">部門別</Radio.Button>
              <Radio.Button value="sales_customer">業績客戶別</Radio.Button>
            </Radio.Group>
          </div>
          <div style={{ marginBottom: 12 }}>
            {fl('單據類別')}
            <Radio.Group value={docType} onChange={e => { setDocType(e.target.value); if (e.target.value === 'bulk' || e.target.value === 'inventory') { setSalesCustFrom(''); setSalesCustTo(''); } }}>
              <Radio.Button value="sample">樣品單</Radio.Button>
              <Radio.Button value="bulk">大貨單</Radio.Button>
              <Radio.Button value="all">全部</Radio.Button>
              <Radio.Button value="inventory">庫存單</Radio.Button>
            </Radio.Group>
          </div>
          <Space wrap size="middle">
            <div>{fl('交運日期起')}<Input value={dateFrom} onChange={e => setDateFrom(e.target.value)} placeholder="YYYY-MM-DD" style={{ width: 140 }} allowClear /></div>
            <div>{fl('交運日期迄')}<Input value={dateTo} onChange={e => setDateTo(e.target.value)} placeholder="YYYY-MM-DD" style={{ width: 140 }} allowClear /></div>
            <div>{fl('業績客戶起')}<Input value={salesCustFrom} onChange={e => setSalesCustFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear disabled={salesCustDisabled} /></div>
            <div>{fl('業績客戶迄')}<Input value={salesCustTo} onChange={e => setSalesCustTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear disabled={salesCustDisabled} /></div>
            <div>{fl('下單客戶起')}<Input value={orderCustFrom} onChange={e => setOrderCustFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear /></div>
            <div>{fl('下單客戶迄')}<Input value={orderCustTo} onChange={e => setOrderCustTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear /></div>
            <div>{fl('產品代號起')}<Input value={productFrom} onChange={e => setProductFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear /></div>
            <div>{fl('產品代號迄')}<Input value={productTo} onChange={e => setProductTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear /></div>
            <div>{fl('部門代號起')}<Input value={deptFrom} onChange={e => setDeptFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear /></div>
            <div>{fl('部門代號迄')}<Input value={deptTo} onChange={e => setDeptTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear /></div>
            <div>{fl('交運單號起')}<Input value={deliveryFrom} onChange={e => setDeliveryFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear /></div>
            <div>{fl('交運單號迄')}<Input value={deliveryTo} onChange={e => setDeliveryTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear /></div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingTop: 18 }}>
              <Space>
                <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery} loading={loading}>查詢</Button>
                <Button icon={<ClearOutlined />} onClick={handleReset}>清除</Button>
              </Space>
            </div>
          </Space>
        </Card>
        {queried && noData && <Empty description="查無此資料" style={{ marginTop: 40 }} />}
        {queried && !noData && hasData && (
          <>
            {renderTable()}
            {renderSummary()}
          </>
        )}
      </div>
    </PermissionGuard>
  );
};

export default C305Form;
